import {useEffect, useState} from 'react';

import {useScreenSize} from '@nfq/react-grid';

/**
 * A custom hook that manages the state and transition logic for a responsive navigation menu.
 * This hook tracks whether the menu is open or closed and determines its transition state based
 * on the current screen size. It distinguishes between desktop (`xxl`, `xl`) and mobile/tablet
 * (`lg`, `md`, `sm`, `xs`) breakpoints and updates the `transition` state accordingly. This makes
 * it suitable for handling mobile slide-out menus, desktop overlays, or hybrid navigation layouts.
 * The hook provides two handlers: `handleToggle` for toggling the menu open/closed state, and
 * `handleClick` for closing the menu directly (e.g., when a menu item is clicked).
 *
 * @returns An object containing:
 * - `isOpen`: Whether the menu is currently open.
 * - `transition`: A mutable ref indicating the current transition state (`'closed'`, `'open'`, `'desktop'`, or `'desktopClosed'`).
 * - `handleToggle`: A function to toggle the menu open/closed state.
 * - `handleClick`: A function to close the menu.
 *
 * @example
 * ```tsx
 * const { isOpen, transition, handleToggle, handleClick } = useMenu();
 *
 * return (
 *   <>
 *     <button onClick={handleToggle}>{isOpen ? 'Close' : 'Open'} Menu</button>
 *     {isOpen && (
 *       <nav data-transition={transition.current}>
 *         <a href="/about" onClick={handleClick}>About</a>
 *       </nav>
 *     )}
 *   </>
 * );
 * ```
 */
export const useMenu = () => {
    const screen = useScreenSize();

    // eslint-disable-next-line @stylistic/max-len
    const [transition, setTransition] = useState<'closed' | 'desktop' | 'desktopClosed' | 'open' | undefined>(undefined);
    const [initial, setInitial] = useState<'closed' | 'desktop' | 'desktopClosed' | 'open' | undefined>('closed');

    /**
     * Toggles the menu state between open and closed.
     * This function inverts the current `isOpen` state, allowing the menu to be opened if it is closed,
     * or closed if it is open. It is typically bound to a button or icon (e.g., a hamburger menu) that
     * controls the visibility of the navigation menu.
     *
     * @example
     * ```tsx
     * <button onClick={handleToggle}>Toggle Menu</button>
     * ```
     */
    const handleToggle = () => {
        if (['open'].includes(transition!)) {
            setTransition('closed');
        } else if (['closed', 'desktopClosed', 'desktop', undefined].includes(transition)) {
            setTransition('open');
        }
    };

    /**
     * Closes the menu by setting `isOpen` to `false`.
     * This function ensures the navigation menu is closed. It is commonly used when a menu item is clicked
     * or when an action should automatically collapse the menu (e.g., after navigation). Unlike `handleToggle`,
     * it always results in the menu being closed, regardless of its prior state.
     *
     * @example
     * ```tsx
     * <a href="/about" onClick={handleClick}>About</a>
     * ```
     */
    const handleClick = () => {
        setTransition('closed');
    };

    useEffect(() => {
        if (['open', 'closed', 'desktopClosed'].includes(transition!) && ['xxl', 'xl'].includes(screen)) {
            setTransition('desktop');
        }
        if (transition === 'desktop' && ['lg', 'md', 'sm', 'xs'].includes(screen)) {
            setTransition('desktopClosed');
        }
    }, [screen, transition]);

    useEffect(() => {
        setTransition(['lg', 'md', 'sm', 'xs'].includes(screen) ? 'desktopClosed' : 'desktop');
        setInitial(['lg', 'md', 'sm', 'xs'].includes(screen) ? 'desktopClosed' : 'desktop');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        handleClick,
        handleToggle,
        initial,
        transition
    };
};